// Template content-form apply service — validate → store update → block
// save batch, for editing content on an already-instantiated project (Plan
// B2 Task 4, cogo.template.v1 content-service loop).
//
// Fail-closed: `validateSlotValues` (Task 4's own value-shape gate, same
// function `instantiate` uses) runs FIRST — any failure aborts before the
// live project's blocks/manifest are ever cloned, so a rejected apply never
// touches the store, the canvas, or the server. Address resolution failures
// (a slot whose declared address has drifted out of the current project
// since instantiation — a "ghost" in this project, distinct from a ghost in
// the template's own authored source) are caught the same way
// `instantiate.ts` catches them and fail the WHOLE apply closed too: no
// partial save, no partial store mutation. A bundle-level record-list
// address (`data/<name>.json`, no owning block — see
// `isUnwritableRecordListAddress`) is rejected the same fail-closed way: it
// resolves fine against the in-memory clone, but there is no server-side
// location this v1 service can persist it to, so treating that as success
// would be a silent data loss (Critical 1 fix — never `ok:true` without a
// real write).
//
// Once PAST all of the above (values valid, every address resolves, every
// touched block is mapped), persistence itself is no longer all-or-nothing:
// each `saveBlock`/`saveProjectMeta` call is awaited individually, and a
// mid-batch rejection stops the remaining saves rather than continuing
// blind. The already-succeeded saves are real server-side writes by that
// point, so they're reflected into the store (never diverging the store
// from the server), while the failed call and everything after it is not —
// the whole apply still resolves `ok:false` with a `persistError` (Critical
// 2 fix).
//
// `templateFormApplyInFlight` (ide-v2-slice.ts) brackets the entire
// validate-through-save sequence — Task 5's divergedAt marking checks this
// exact flag.

import type { AppBundle, Json, TypedFailure } from '@cogo/template'
import { applyAtAddress, validateSlotValues } from '@cogo/template'
import type { TemplateRegistryEntry } from '../registry'
import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { withEditorAutoSaveSuppressed } from '@/store/auto-save-bridge'
import * as api from '@/services/project-api'
import { blockIdForRecordListFile, bundleFilesFromProjectSource, isUnwritableRecordListAddress } from './slot-form-model'

export interface ApplyResult {
  ok: boolean
  failures: TypedFailure[]
  /** blockIds whose ui/data were successfully saved (best-effort — populated even on a later partial failure, so callers can see how far it got). */
  savedBlocks: string[]
  /**
   * Set when persistence itself (a `saveBlock`/`saveProjectMeta` call, past
   * every fail-closed validation stage) rejected mid-batch. `savedBlocks`
   * lists everything that landed server-side BEFORE the rejection — the
   * store is already reflected with exactly those, so this is purely for
   * the caller to surface an honest "some changes weren't saved" notice.
   */
  persistError?: string
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Mirrors `blockIds`' ui/data and (when `manifestChanged`) `app.json` from
 * `files` into both live stores — the canvas (`useEditorStore`) first, then
 * the studio-store project mirror kept alongside it. Callers choose exactly
 * which blockIds/manifest flag to pass so this can reflect either the FULL
 * touched set (full success) or only the subset that's actually landed
 * server-side (a mid-batch persistence rejection) — see `applySlotValues`.
 *
 * The `useEditorStore.setState` call is wrapped in `withEditorAutoSaveSuppressed`
 * (review finding, Plan B2 Task 5 follow-up): every value here has ALREADY
 * been persisted by `applySlotValues`'s own `saveBlock`/`saveProjectMeta`
 * calls above — this mutation is purely a display mirror. Without the
 * suppression, this mutation re-enters `auto-save-bridge.ts`'s EditorStore
 * subscription synchronously, which — with no knowledge of
 * `templateFormApplyInFlight` — queues a REDUNDANT debounced block save via
 * `queueSave`. That save flushes ~500ms later, by which point
 * `templateFormApplyInFlight` has already reset to false (this function's
 * caller's `finally` runs synchronously right after `reflectAppliedChanges`
 * returns), so `markTemplateDivergenceOnForeignEdit`
 * (`project-lineage.ts`, wired into `auto-save.ts`'s `dispatchEntries`) sees
 * an ordinary-looking queued block save and marks `divergedAt` on a
 * perfectly legitimate template-form apply — the exact false-positive this
 * suppression exists to prevent.
 */
function reflectAppliedChanges(files: AppBundle['files'], blockIds: Iterable<string>, manifestChanged: boolean): void {
  withEditorAutoSaveSuppressed(() => {
    useEditorStore.setState((state) => {
      for (const blockId of blockIds) {
        const block = state.blocks[blockId]
        if (!block) continue
        const uiKey = `blocks/${blockId}/ui.json`
        const dataKey = `blocks/${blockId}/data.json`
        if (uiKey in files) block.ui = files[uiKey] as never
        if (dataKey in files) block.data = files[dataKey] as never
      }
      if (manifestChanged && state.app && isPlainObject(files['app.json'])) {
        Object.assign(state.app, files['app.json'])
      }
    })
  })

  useStudioStore.setState((s) => {
    const project = s.currentProject as unknown as { blocks?: Record<string, { ui?: unknown; data?: unknown }>; manifest?: unknown } | null
    if (!project) return
    for (const blockId of blockIds) {
      const block = project.blocks?.[blockId]
      if (!block) continue
      const uiKey = `blocks/${blockId}/ui.json`
      const dataKey = `blocks/${blockId}/data.json`
      if (uiKey in files) block.ui = files[uiKey]
      if (dataKey in files) block.data = files[dataKey]
    }
    if (manifestChanged && isPlainObject(project.manifest) && isPlainObject(files['app.json'])) {
      Object.assign(project.manifest, files['app.json'])
    }
  })
}

/**
 * Validates `values` against `entry.pkg`, applies them to the currently
 * open project's live content (page-editor's `useEditorStore`), persists
 * every touched block via `saveBlock` and a touched manifest via
 * `saveProjectMeta` (the same path `persistCurrentPublishEvidence` /
 * `publishCurrentProject` use for app.json-addressed fields), and mirrors
 * the result back into both `useEditorStore` (so the canvas reflects
 * immediately) and `useStudioStore().currentProject` (kept as a project
 * mirror by the rest of the store).
 *
 * Fail-closed at every stage:
 *  - `validateSlotValues` failures → nothing touched, `templateFormApplyInFlight` never even flips true.
 *  - no open project → typed failure, same as above.
 *  - an address that doesn't resolve in the CURRENT project → the whole
 *    apply aborts before any `saveBlock`/`saveProjectMeta` call.
 *  - a slot's `blockId` isn't a block that exists in this project (registry
 *    entry vs. project drift) → same fail-closed abort.
 */
export async function applySlotValues(
  entry: TemplateRegistryEntry,
  values: Record<string, Json>,
): Promise<ApplyResult> {
  const shapeFailures = validateSlotValues(entry.pkg, values)
  if (shapeFailures.length > 0) {
    return { ok: false, failures: shapeFailures, savedBlocks: [] }
  }

  const studio = useStudioStore.getState()
  const projectId = studio.currentProjectId
  const editor = useEditorStore.getState()
  if (!projectId || !editor.app) {
    return {
      ok: false,
      failures: [{ code: 'template-apply-no-project', message: 'No open project to apply slot values to' }],
      savedBlocks: [],
    }
  }

  studio.setTemplateFormApplyInFlight(true)
  try {
    const { blocks, appJson } = bundleFilesFromProjectSource({
      manifest: editor.app,
      blocks: editor.blocks,
      data: editor.data,
    })
    const files: AppBundle['files'] = structuredClone({ ...blocks, 'app.json': appJson })

    const bySlotId = new Map(entry.pkg.slots.map((s) => [s.slotId, s]))
    const touchedBlockIds = new Set<string>()
    let manifestTouched = false
    const addressFailures: TypedFailure[] = []

    for (const slotId of Object.keys(values)) {
      const slot = bySlotId.get(slotId)
      // Unreachable in practice: validateSlotValues above already rejects
      // any key that isn't a declared slot id.
      if (!slot) continue
      const addresses = Array.isArray(slot.address) ? slot.address : [slot.address]
      for (const address of addresses) {
        // Bundle-level `data/<name>.json` record-lists have no owning block
        // in this project's schema (StudioProjectBundleBlock has no slot for
        // arbitrary bundle-level static-datasource files), and instance
        // projects don't carry a template's bundle data files at all (Plan
        // B1's createProject drops LoadedApp.data) — there is no honest
        // server-side write for this v1 service to make. `applyAtAddress`
        // would happily resolve and mutate the in-memory clone (it has no
        // way to know that clone is never persisted), so this check MUST run
        // before calling it — reusing `slot-address-missing` per the same
        // closed-set TypedFailure code `resolve-address.ts`'s own throw path
        // uses for every other "this address doesn't resolve to a real
        // target" case.
        if (isUnwritableRecordListAddress(address)) {
          addressFailures.push({
            code: 'slot-address-missing',
            slotId,
            message: `slot "${slotId}" addresses "${(address as { file: string }).file}", a bundle-level data file this project doesn't carry — instance projects don't have template bundle data files in v1, so this slot can't be edited`,
          })
          continue
        }
        try {
          applyAtAddress(files, address, values[slotId])
        } catch (err) {
          addressFailures.push({
            code: 'slot-address-missing',
            slotId,
            message: err instanceof Error ? err.message : String(err),
          })
          continue
        }
        if (address.kind === 'element') {
          touchedBlockIds.add(address.blockId)
        } else if (address.kind === 'app' || address.kind === 'datasource') {
          manifestTouched = true
        } else if (address.kind === 'record-list') {
          const blockId = blockIdForRecordListFile(address.file)
          if (blockId) touchedBlockIds.add(blockId)
        }
      }
    }

    if (addressFailures.length > 0) {
      return { ok: false, failures: addressFailures, savedBlocks: [] }
    }

    // Fail-closed, part 2: resolve every touched block's DB id BEFORE any
    // saveBlock is issued. Checking this inside the save loop itself would
    // let earlier blocks' saveBlock calls already have landed on the server
    // by the time a later block turns out unmapped — a real partial
    // persistence, not just a discarded in-memory bundle. `touchedBlockIds`
    // is only ever populated after `applyAtAddress` has already confirmed
    // `editor.blocks[blockId]` exists, so a miss here means blockIdMap
    // itself is stale relative to the live canvas — a distinct drift class
    // from a ghost slot address.
    const missingBlockFailures: TypedFailure[] = []
    for (const blockId of touchedBlockIds) {
      if (!studio.blockIdMap[blockId]) {
        missingBlockFailures.push({
          code: 'template-apply-block-missing',
          message: `Block "${blockId}" is not present in this project`,
        })
      }
    }
    if (missingBlockFailures.length > 0) {
      return { ok: false, failures: missingBlockFailures, savedBlocks: [] }
    }

    // Persistence, part 1/2: block saves. Every fail-closed validation stage
    // above has already passed by this point (values, addresses, blockId
    // mapping) — from here on a rejection is a REAL infrastructure failure
    // (network, server), not a data-shape problem, and by definition can
    // only happen mid-batch: earlier calls in this same loop may already
    // have landed server-side. Each save is awaited and caught individually
    // (never a bare `await` inside a loop with no per-iteration guard) so a
    // rejection stops the remaining saves immediately instead of racing
    // ahead — `persistError` short-circuits both this loop and the
    // manifest save below, and `savedBlocks` ends up holding exactly the
    // subset that's real on the server, which is what gets reflected into
    // the store (never more than what actually persisted).
    const savedBlocks: string[] = []
    let persistError: string | undefined
    for (const blockId of touchedBlockIds) {
      const blockDbId = studio.blockIdMap[blockId]
      const uiKey = `blocks/${blockId}/ui.json`
      const dataKey = `blocks/${blockId}/data.json`
      // saveBlock's DB column for a block's data.json content is called
      // "datasource" (block-file-contract.ts's `data` descriptor), NOT
      // "data" — that field name only exists on the in-memory BlockDef.
      const updates: Partial<Record<'ui' | 'datasource', unknown>> = {}
      if (uiKey in files) updates.ui = files[uiKey]
      if (dataKey in files) updates.datasource = files[dataKey]
      if (Object.keys(updates).length === 0) continue
      try {
        await api.saveBlock(projectId, blockDbId, updates)
        savedBlocks.push(blockId)
      } catch (err) {
        persistError = err instanceof Error ? err.message : String(err)
        break
      }
    }

    // Persistence, part 2/2: manifest save — only attempted when the block
    // loop above ran clean. `manifestSaved` (distinct from `manifestTouched`,
    // which just means a slot's address TARGETED app.json) tracks whether
    // this call actually landed, so the reflect step below never mirrors a
    // manifest change the server never received.
    let manifestSaved = false
    if (!persistError && manifestTouched) {
      try {
        await api.saveProjectMeta(projectId, { manifest: files['app.json'] })
        manifestSaved = true
      } catch (err) {
        persistError = err instanceof Error ? err.message : String(err)
      }
    }

    if (persistError) {
      // Reflect ONLY what's real on the server — this is what keeps the
      // store from diverging from a partial batch (Critical 2 fix): the
      // blocks in `savedBlocks` truly landed, so mirroring them is correct;
      // anything after the failure point did not, so it stays un-reflected
      // (and, since `handleApply` never clears dirty state on a `!ok`
      // result, stays visibly pending in the form too).
      reflectAppliedChanges(files, savedBlocks, manifestSaved)
      return {
        ok: false,
        failures: [{ code: 'template-apply-persist-failed', message: persistError }],
        savedBlocks,
        persistError,
      }
    }

    // Full success: reflect immediately into both live stores — the canvas
    // (useEditorStore) first, then the studio-store project mirror kept
    // alongside it.
    reflectAppliedChanges(files, touchedBlockIds, manifestTouched)

    return { ok: true, failures: [], savedBlocks }
  } finally {
    studio.setTemplateFormApplyInFlight(false)
  }
}
